export interface BookingAssignmentPagedResult<T> {
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  isLastPage: boolean;
  items: T[];
}

export interface BookingAssignment {
  bookingNumber: string;
  maintenanceShopCode: string;
  carrier?: string;
  bookingType?: string;
  departureDate?: string;
  totalBookedEquipmentQuantity: number;
  totalAssignedEquipmentQuantity: number;
  nonCompliantEquipmentQuantity: number;
  destinationLocationCity?: string;
  updateTimestamp?: string;
}

export interface BookingAssignmentFilters {
  bookingNumber?: string;
  assignmentStatus?: string[];
}

export const BOOKING_ASSIGNMENT_STATUSES = ['None Assigned', 'Partially Assigned', 'Fully Assigned'] as const;

/** Steps in the assign-container wizard, driven by a config array in the shell. */
export type BookingAssignmentStepId = 'recommended' | 'manual' | 'final';

export interface BookingAssignmentStep {
  id: BookingAssignmentStepId;
  label: string;
}

export const BOOKING_ASSIGNMENT_STEPS: BookingAssignmentStep[] = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'manual', label: 'Manual' },
  { id: 'final', label: 'Final' },
];

/** Validation outcome for a manually-entered container. */
export type ContainerValidationStatus = 'Passed' | 'Failed' | 'Exception';

/** A single container row shown in the recommended / manual / final grids. */
export interface ContainerRow {
  equipmentNumber: string;
  equipmentType?: string;
  grade?: string;
  size?: string;
  height?: string;
  forSale?: boolean;
  redeliveryDate?: string;
  inDepoSince?: string;
  ptiStatus?: string;
  validationStatus?: ContainerValidationStatus;
  /** Which wizard step this container was selected from. */
  source?: 'Recommended' | 'Manual';
  /** Action applied to this container relative to the original booking assignment. */
  action?: 'Added' | 'Removed' | 'No Action';
  /** Audit/assignment ID from the eligibility-check API — only present for Manual-sourced rows. */
  auditId?: string | null;
  remarks?: string | null;
  comments?: string | null;
}

/** Filters available on the Recommended step. */
export interface RecommendedContainerFilters {
  equipmentType?: string[];
  grade?: string[];
  size?: string[];
  height?: string[];
  forSale?: string | null;
  redelivery?: string | null;
}

/** Per equipment-type allocation progress shown in the right-rail summary. */
export interface AllocationSummaryItem {
  equipmentType: string;
  size?: string;
  height?: string;
  hasFoodGrade: boolean;
  assignedQuantity: number;
  bookedQuantity: number;
}

// ─── API response interfaces (matching the .NET BookingAssignmentDetailResponse contract) ───

export interface BookingEquipmentPropertyResponse {
  equipmentTypeName: string;
  equipmentSize: number;
  equipmentHeight: string;
  totalQuantity: number;
  assignedQuantity: number;
  gradeToQuantityMap: Record<string, number>;
}

export interface AssignedEquipmentResponse {
  equipmentNumber: string;
  equipmentTypeName: string;
  equipmentSize: number;
  equipmentOutsideHeight: string;
  equipmentGradeCode: string;
  isBookable: boolean;
  sequenceNumber: number;
  createUserId: string | null;
  createTimestamp: string | null;
  remarks: string | null;
  comments: string | null;
}

export interface EquipmentRecommendationResponse {
  equipmentNumber: string;
  equipmentTypeName: string;
  sizeCode: number | null;
  equipmentHeight: string | null;
  gradeCode: string | null;
  isTaggedForSelling: boolean | null;
  isTaggedForRedelivery: boolean | null;
  daysAtLocation: number | null;
}

export interface BookingAssignmentDetailResponse {
  bookingNumber: string;
  departureDate: string | null;
  totalBookedEquipmentQuantity: number;
  totalAssignedEquipmentQuantity: number;
  nonCompliantEquipmentQuantity: number;
  createTimestamp: string | null;
  createUserId: string | null;
  updateTimestamp: string | null;
  updateUserId: string | null;
  destinationLocationCity: string | null;
  equipmentPropertyList: BookingEquipmentPropertyResponse[];
  assignedEquipmentList: AssignedEquipmentResponse[];
  equipmentRecommendationList: EquipmentRecommendationResponse[];
}

// ─── Eligibility check (Manual step) ────────────────────────────────────────

export type ManualValidationStatus = 'Passed' | 'Failed' | 'Warning';

/** API response from POST maintenance-shops/{shopCode}/bookings/{bookingNumber}/equipment/{eq}/assignment-audits */
export interface EligibilityCheckResponse {
  auditId: string;
  maintenanceShopCode: string;
  bookingNumber: string;
  equipmentNumber: string;
  equipmentTypeName: string | null;
  equipmentSize: number | null;
  equipmentOutsideHeight: string | null;
  equipmentGradeCode: string | null;
  isBookable: boolean;
  isRedeliveryEligible: boolean | null;
  isSaleEligible: boolean | null;
  hasRedeliveryDemandAtSource: boolean | null;
  hasSaleDemandAtSource: boolean | null;
  isPremiumQuality: boolean | null;
  isTotalLoss: boolean | null;
  isAtOffhireLocation: boolean | null;
  sequenceNumber: number | null;
  errorType: string | null;
  remarks: string | null;
  createUserId: string | null;
  createTimestamp: string | null;
}

/** Row shape for the manually-entered container results grid. */
export interface ManualStepRow {
  equipmentNumber: string;
  auditId?: string | null;
  grade: string;
  size: string;
  type: string;
  height: string;
  validation: ManualValidationStatus;
  remarks?: string | null;
  /** Exception comment entered by the user for Warning rows; included in the final submission payload. */
  comments?: string | null;
  /** mc-table reads this to disable the row checkbox when true. */
  selectDisabled?: boolean;
}

/** Payload item for POST maintenance-shops/{shopCode}/bookings/{bookingNumber}/booking-equipment. */
export interface SubmitBookingAssignmentsRequestItem {
  assignmentId?: string | null;
  equipmentNumber: string;
  remarks?: string | null;
  comments?: string | null;
}

/** A normalised row in the combined recommended-step table (pre-assigned + fresh recommendations). */
export interface RecommendedStepRow {
  equipmentNumber: string;
  equipmentType: string;
  grade: string;
  size: string;
  height: string;
  sale: string;
  redelivery: string;
  inDepoSince: string;
  /** True for containers already assigned before this session — their selection is locked. */
  isAssigned: boolean;
}
